import {
  AppModal,
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  dashboardActionOptionsQueryOptions,
  dashboardDataItemOptionsQueryOptions,
  dashboardPhaseOptionsQueryOptions,
} from "@/lib/data/react-query/matters/dashboard-tables";
import type {
  DashboardPhaseOption,
  DashboardActionColumn,
  DashboardDataColumn,
  DashboardTableType,
  MatterDashboardTable,
} from "@/lib/types/MatterDashboardTable";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { Link } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { Search, X } from "lucide-react";
import { useMemo, useState } from "react";

import { DashboardSortableActionRow } from "./DashboardSortableActionRow";

type TableSave = {
  title: string;
  type?: DashboardTableType;
  phaseIds: string[];
  excludedActionDefinitionIds: string[];
  dataItemIds: string[];
  orderedColumnKeys?: string[];
};

type Props = {
  firmId: string;
  userId: string;
  table?: MatterDashboardTable;
  onClose: () => void;
  onSave: (value: TableSave) => Promise<void>;
};

export function DashboardTableEditorModal({
  firmId,
  userId,
  table,
  onClose,
  onSave,
}: Props) {
  const [title, setTitle] = useState(table?.title ?? "");
  const [type, setType] = useState<DashboardTableType>(
    table?.type ?? "matter_actions",
  );
  const [phaseIds, setPhaseIds] = useState(table?.phaseIds ?? []);
  const [selectedPhases, setSelectedPhases] = useState<DashboardPhaseOption[]>(
    table?.phases ?? [],
  );
  const [excludedIds, setExcludedIds] = useState(
    table?.excludedActionDefinitionIds ?? [],
  );
  const [dataItemIds, setDataItemIds] = useState(table?.dataItemIds ?? []);
  const [orderedKeys, setOrderedKeys] = useState(
    table?.orderedColumnKeys ?? table?.columnOrder ?? [],
  );
  const [orderTouched, setOrderTouched] = useState(false);
  const [phaseSearch, setPhaseSearch] = useState("");
  const debouncedPhaseSearch = useDebouncedValue(phaseSearch, 300);
  const normalizedPhaseSearch = phaseSearch.trim().toLocaleLowerCase();
  const isPhaseSearchPending =
    phaseSearch.trim() !== debouncedPhaseSearch.trim();
  const [phasePage, setPhasePage] = useState(1);
  const [error, setError] = useState("");
  const [errorField, setErrorField] = useState<
    "title" | "phases" | "actions" | null
  >(null);
  const [saving, setSaving] = useState(false);
  const [createOutcomeUnknown, setCreateOutcomeUnknown] = useState(false);
  const phaseQuery = useQuery(
    dashboardPhaseOptionsQueryOptions(
      firmId,
      userId,
      debouncedPhaseSearch.trim(),
      phasePage,
      50,
    ),
  );
  const phases = useMemo(() => {
    const byId = new Map<string, DashboardPhaseOption>();
    for (const phase of selectedPhases) {
      if (phase.title.toLocaleLowerCase().includes(normalizedPhaseSearch)) {
        byId.set(phase.id, phase);
      }
    }
    for (const phase of phaseQuery.data?.data ?? []) {
      if (phase.title.toLocaleLowerCase().includes(normalizedPhaseSearch)) {
        byId.set(phase.id, phase);
      }
    }
    return [...byId.values()];
  }, [normalizedPhaseSearch, phaseQuery.data?.data, selectedPhases]);
  const activePhaseIds = phaseIds.filter((id) =>
    selectedPhases.some(
      (phase) => phase.id === id && phase.lifecycle === "Active",
    ),
  );
  const actionQuery = useQuery({
    ...dashboardActionOptionsQueryOptions(firmId, userId, activePhaseIds),
    enabled: activePhaseIds.length > 0,
  });
  const phaseActionQueries = useQueries({
    queries:
      activePhaseIds.length > 1
        ? activePhaseIds.map((phaseId) =>
            dashboardActionOptionsQueryOptions(firmId, userId, [phaseId]),
          )
        : [],
  });
  const dataItemQuery = useQuery(
    dashboardDataItemOptionsQueryOptions(firmId, userId),
  );
  const actions =
    activePhaseIds.length > 0
      ? (actionQuery.data?.data ?? [])
      : (table?.columns ?? []);
  const actionPhaseTitles = useMemo(() => {
    const titles = new Map<string, string[]>();
    activePhaseIds.forEach((phaseId, index) => {
      const phaseTitle = selectedPhases.find(
        (phase) => phase.id === phaseId,
      )?.title;
      if (!phaseTitle) return;
      const phaseActions =
        activePhaseIds.length === 1
          ? actionQuery.data?.data
          : phaseActionQueries[index]?.data?.data;
      for (const action of phaseActions ?? []) {
        const existing = titles.get(action.actionDefinitionId) ?? [];
        titles.set(action.actionDefinitionId, [...existing, phaseTitle]);
      }
    });
    return titles;
  }, [
    activePhaseIds,
    actionQuery.data?.data,
    phaseActionQueries,
    selectedPhases,
  ]);
  const dataOptions = useMemo(() => {
    const byId = new Map<string, DashboardDataColumn>();
    for (const item of table?.dataColumns ?? []) byId.set(item.id, item);
    for (const item of dataItemQuery.data?.data ?? []) byId.set(item.id, item);
    return [...byId.values()];
  }, [dataItemQuery.data?.data, table?.dataColumns]);
  const selectedDataColumns = dataItemIds
    .map((id) => dataOptions.find((item) => item.id === id))
    .filter((item): item is DashboardDataColumn => Boolean(item));
  const sortBySavedOrder = <
    T extends DashboardActionColumn | DashboardDataColumn,
  >(
    items: T[],
  ) =>
    [...items].sort((a, b) => {
      const aIndex = orderedKeys.indexOf(a.key);
      const bIndex = orderedKeys.indexOf(b.key);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  const sortedActions = sortBySavedOrder(actions);
  const configurableColumns = sortBySavedOrder(
    type === "matter_actions"
      ? [
          ...actions.filter(
            (action) => !excludedIds.includes(action.actionDefinitionId),
          ),
          ...selectedDataColumns,
        ]
      : selectedDataColumns,
  );
  const excludedActions = actions.filter((action) =>
    excludedIds.includes(action.actionDefinitionId),
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const isDirty =
    title !== (table?.title ?? "") ||
    (!table && type !== "matter_actions") ||
    JSON.stringify(phaseIds) !== JSON.stringify(table?.phaseIds ?? []) ||
    JSON.stringify(excludedIds) !==
      JSON.stringify(table?.excludedActionDefinitionIds ?? []) ||
    JSON.stringify(dataItemIds) !== JSON.stringify(table?.dataItemIds ?? []) ||
    orderTouched;

  const togglePhase = (phase: DashboardPhaseOption) => {
    setError("");
    setErrorField(null);
    if (phaseIds.includes(phase.id)) {
      setPhaseIds((current) => current.filter((id) => id !== phase.id));
      setSelectedPhases((current) =>
        current.filter((item) => item.id !== phase.id),
      );
    } else {
      setPhaseIds((current) => [...current, phase.id]);
      setSelectedPhases((current) => [...current, phase]);
    }
  };

  const reorderColumns = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;
    const ids = configurableColumns.map((item) => item.key);
    const from = ids.indexOf(String(event.active.id));
    const to = ids.indexOf(String(event.over.id));
    if (from < 0 || to < 0) return;
    const reordered = arrayMove(ids, from, to);
    setOrderedKeys(reordered);
    setOrderTouched(true);
  };

  const save = async () => {
    if (saving || createOutcomeUnknown) return;
    if (!title.trim()) {
      setErrorField("title");
      setError("Table name cannot be blank.");
      return;
    }
    if (phaseIds.length === 0) {
      setErrorField("phases");
      setError("Select at least one phase.");
      return;
    }
    const actionSelectionsChanged =
      !table ||
      JSON.stringify(phaseIds) !== JSON.stringify(table.phaseIds) ||
      JSON.stringify(excludedIds) !==
        JSON.stringify(table.excludedActionDefinitionIds);
    if (actionSelectionsChanged && activePhaseIds.length === 0) {
      setErrorField("actions");
      setError("Wait for the phase actions to load, then try again.");
      return;
    }
    if (
      dataItemIds.length === 0 &&
      (actionQuery.isPending || actionQuery.isError)
    ) {
      setErrorField("actions");
      setError("Wait for the phase actions to load, then try again.");
      return;
    }
    if (
      dataItemIds.length === 0 &&
      actions.every((action) => excludedIds.includes(action.actionDefinitionId))
    ) {
      setErrorField("actions");
      setError("Select at least one phase action or Matter data item.");
      return;
    }
    setSaving(true);
    setError("");
    setErrorField(null);
    try {
      await onSave({
        title: title.trim(),
        ...(!table && type === "unassigned_actions" ? { type } : {}),
        phaseIds,
        excludedActionDefinitionIds: excludedIds,
        dataItemIds,
        ...(orderTouched
          ? {
              orderedColumnKeys: configurableColumns.map(
                (column) => column.key,
              ),
            }
          : {}),
      });
    } catch (failure) {
      const ambiguousCreate =
        !table && (!isAxiosError(failure) || !failure.response);
      setCreateOutcomeUnknown(ambiguousCreate);
      setError(
        ambiguousCreate
          ? "The response was lost, so this table may have been created. Close this dialog and check the table list before trying again."
          : isAxiosError(failure) && failure.response?.status === 409
            ? "This table changed elsewhere. Close and reopen it to review the latest version."
            : isAxiosError(failure) && failure.response?.status === 400
              ? "These selections could not be saved. Review the phases and actions."
              : "Unable to save the table. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
      title={table ? "Edit table" : "Add table"}
      size="2xl"
      isDirty={isDirty}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || createOutcomeUnknown}
          >
            Done
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div>
          <label
            htmlFor="dashboard-table-type"
            className="mb-2 block text-sm font-medium"
          >
            Table type
          </label>
          <Select
            value={type}
            disabled={Boolean(table)}
            onValueChange={(value: DashboardTableType) => {
              setType(value);
            }}
          >
            <SelectTrigger id="dashboard-table-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="matter_actions">Matter actions</SelectItem>
              <SelectItem value="unassigned_actions">
                Unassigned actions
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label
            htmlFor="dashboard-table-title"
            className="mb-2 block text-sm font-medium"
          >
            Table name <span className="text-destructive">*</span>
          </label>
          <Input
            id="dashboard-table-title"
            value={title}
            maxLength={200}
            aria-invalid={errorField === "title"}
            aria-describedby={
              errorField === "title" ? "dashboard-table-title-error" : undefined
            }
            onChange={(event) => {
              setTitle(event.target.value);
              setError("");
              setErrorField(null);
            }}
          />
          {errorField === "title" && (
            <p
              id="dashboard-table-title-error"
              className="text-destructive mt-1 text-sm"
              role="alert"
            >
              {error}
            </p>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium">
              Phases <span className="text-destructive">*</span>
            </h3>
            <span className="text-muted-foreground text-xs">
              {phaseIds.length} selected
            </span>
          </div>
          <div className="relative mt-3">
            <Search
              aria-hidden="true"
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
            />
            <Input
              type="search"
              aria-label="Search phases"
              placeholder="Search phases"
              value={phaseSearch}
              onChange={(event) => {
                setPhaseSearch(event.target.value);
                setPhasePage(1);
              }}
              className="bg-background pr-10 pl-9 [&::-webkit-search-cancel-button]:hidden"
            />
            {phaseSearch && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Clear phase search"
                className="text-muted-foreground absolute top-1/2 right-1 size-8 -translate-y-1/2"
                onClick={() => {
                  setPhaseSearch("");
                  setPhasePage(1);
                }}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
          <div className="mt-2 max-h-48 divide-y overflow-y-auto rounded-lg border">
            {phaseQuery.isPending && (
              <p className="text-muted-foreground p-3 text-sm">
                Loading phases…
              </p>
            )}
            {phaseQuery.isError && (
              <p className="text-destructive p-3 text-sm">
                Unable to load phases.
              </p>
            )}
            {phases.length === 0 &&
              !isPhaseSearchPending &&
              !phaseQuery.isPending &&
              !phaseQuery.isError && (
                <p className="text-muted-foreground p-3 text-sm">
                  {debouncedPhaseSearch ? (
                    "No phases match the search."
                  ) : (
                    <>
                      No active phases found. Add a phase in{" "}
                      <Link
                        to="/matters/settings/phase-projects"
                        search={{
                          editor: undefined,
                          edit: undefined,
                          q: undefined,
                          lifecycle: undefined,
                          incidentTypeId: undefined,
                          pageNumber: undefined,
                          pageSize: undefined,
                        }}
                        className="text-primary underline"
                      >
                        Matter phases
                      </Link>
                      .
                    </>
                  )}
                </p>
              )}
            {phases.map((phase) => (
              <label
                key={phase.id}
                className="hover:bg-muted/50 flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2 text-sm has-[:disabled]:cursor-not-allowed"
              >
                <Checkbox
                  checked={phaseIds.includes(phase.id)}
                  disabled={
                    phase.lifecycle === "Archived" &&
                    !phaseIds.includes(phase.id)
                  }
                  onCheckedChange={() => {
                    togglePhase(phase);
                  }}
                />
                <span className="min-w-0 flex-1">{phase.title}</span>
                {phase.lifecycle === "Archived" && (
                  <span className="text-muted-foreground text-xs">
                    Archived
                  </span>
                )}
              </label>
            ))}
          </div>
          {(phaseQuery.data?.pagination?.count ?? 0) > 50 && (
            <div className="mt-2 flex items-center justify-end gap-2 text-sm">
              <Button
                variant="ghost"
                size="xs"
                disabled={phasePage === 1}
                onClick={() => {
                  setPhasePage((page) => page - 1);
                }}
              >
                Previous
              </Button>
              <span>{phasePage}</span>
              <Button
                variant="ghost"
                size="xs"
                disabled={
                  phasePage * 50 >= (phaseQuery.data?.pagination?.count ?? 0)
                }
                onClick={() => {
                  setPhasePage((page) => page + 1);
                }}
              >
                Next
              </Button>
            </div>
          )}
          {errorField === "phases" && (
            <p className="text-destructive mt-2 text-sm" role="alert">
              {error}
            </p>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium">Matter data</h3>
            <span className="text-muted-foreground text-xs">
              {dataItemIds.length} selected
            </span>
          </div>
          <div className="mt-3 divide-y rounded-lg border">
            {dataItemQuery.isPending && (
              <p className="text-muted-foreground p-3 text-sm">
                Loading matter data…
              </p>
            )}
            {dataItemQuery.isError && (
              <p className="text-destructive p-3 text-sm">
                Unable to load Matter data.
              </p>
            )}
            {dataOptions.map((item) => (
              <label
                key={item.id}
                className="hover:bg-muted/50 flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2 text-sm has-[:disabled]:cursor-not-allowed"
              >
                <Checkbox
                  checked={dataItemIds.includes(item.id)}
                  disabled={!item.available && !dataItemIds.includes(item.id)}
                  onCheckedChange={() => {
                    setDataItemIds((current) =>
                      current.includes(item.id)
                        ? current.filter((id) => id !== item.id)
                        : [...current, item.id],
                    );
                    setError("");
                    setErrorField(null);
                  }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block">{item.title}</span>
                  {item.description && (
                    <span className="text-muted-foreground block text-xs">
                      {item.description}
                    </span>
                  )}
                </span>
                {!item.available && (
                  <span className="text-muted-foreground text-xs">
                    Unavailable
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>
        {phaseIds.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-medium">
              {type === "matter_actions" ? "Columns" : "Phase actions"}
            </h3>
            <div className="max-h-64 overflow-y-auto rounded-lg border">
              {activePhaseIds.length > 0 && actionQuery.isPending && (
                <p className="text-muted-foreground p-2 text-sm">
                  Loading actions…
                </p>
              )}
              {activePhaseIds.length > 0 && actionQuery.isError && (
                <p className="text-destructive p-2 text-sm">
                  Unable to load actions for these phases.
                </p>
              )}
              {actions.length === 0 &&
                !actionQuery.isPending &&
                !actionQuery.isError && (
                  <p className="text-muted-foreground p-2 text-sm">
                    These phases have no available actions.
                  </p>
                )}
              {type === "unassigned_actions" && (
                <div className="divide-y">
                  {sortedActions.map((action) => (
                    <DashboardSortableActionRow
                      key={action.key}
                      action={action}
                      phaseTitles={actionPhaseTitles.get(
                        action.actionDefinitionId,
                      )}
                      reorderable={false}
                      checked={!excludedIds.includes(action.actionDefinitionId)}
                      onCheckedChange={() => {
                        setExcludedIds((current) =>
                          current.includes(action.actionDefinitionId)
                            ? current.filter(
                                (id) => id !== action.actionDefinitionId,
                              )
                            : [...current, action.actionDefinitionId],
                        );
                      }}
                    />
                  ))}
                </div>
              )}
              {type === "matter_actions" && (
                <>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={reorderColumns}
                  >
                    <SortableContext
                      items={configurableColumns.map((column) => column.key)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="divide-y">
                        {configurableColumns.map((column) => (
                          <DashboardSortableActionRow
                            key={column.key}
                            action={column}
                            phaseTitles={
                              column.kind === "phaseAction"
                                ? actionPhaseTitles.get(
                                    column.actionDefinitionId,
                                  )
                                : undefined
                            }
                            checked
                            onCheckedChange={() => {
                              if (column.kind === "matterData") {
                                setDataItemIds((current) =>
                                  current.filter((id) => id !== column.id),
                                );
                              } else {
                                setExcludedIds((current) => [
                                  ...current,
                                  column.actionDefinitionId,
                                ]);
                              }
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {excludedActions.length > 0 && (
                    <div className="divide-y border-t">
                      {excludedActions.map((column) => (
                        <DashboardSortableActionRow
                          key={column.key}
                          action={column}
                          phaseTitles={actionPhaseTitles.get(
                            column.actionDefinitionId,
                          )}
                          checked={false}
                          reorderable={false}
                          onCheckedChange={() => {
                            setExcludedIds((current) =>
                              current.filter(
                                (id) => id !== column.actionDefinitionId,
                              ),
                            );
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            {type === "unassigned_actions" &&
              selectedDataColumns.length > 1 && (
                <div className="mt-3">
                  <h3 className="mb-2 text-sm font-medium">
                    Matter data order
                  </h3>
                  <div className="divide-y rounded-lg border">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      modifiers={[restrictToVerticalAxis]}
                      onDragEnd={reorderColumns}
                    >
                      <SortableContext
                        items={configurableColumns.map((column) => column.key)}
                        strategy={verticalListSortingStrategy}
                      >
                        {configurableColumns.map((column) => (
                          <DashboardSortableActionRow
                            key={column.key}
                            action={column}
                            checked
                            onCheckedChange={() => {
                              if (column.kind === "matterData") {
                                setDataItemIds((current) =>
                                  current.filter((id) => id !== column.id),
                                );
                              }
                            }}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                </div>
              )}
            {errorField === "actions" && (
              <p className="text-destructive mt-2 text-sm" role="alert">
                {error}
              </p>
            )}
          </div>
        )}
        {error && !errorField && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
      </div>
    </AppModal>
  );
}
